// components/ConfigurationTab.tsx
/**
 * Configuration Tab Component
 *
 * Handles field-level configuration including:
 * - Field visibility toggling
 * - Label editing
 * - Field reordering
 * - Table column management
 */

import { useActiveUtilityServices } from "@shared/hooks/useActiveUtililyServices";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { ScrollArea } from "@shared/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Separator } from "@shared/ui/separator";
import { Slider } from "@shared/ui/slider";
import { Switch } from "@shared/ui/switch";
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  FileText,
  Table,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { FieldDefinition, GraphConfig, NormalizedComponent } from "../../types";

interface ConfigurationTabProps {
  components: NormalizedComponent[];
  selectedComponentId: string | null;
  onFieldUpdate: (componentId: string, fieldName: string, updates: any) => void;
  onToggleField: (componentId: string, fieldName: string) => void;
  onToggleLabel: (componentId: string, fieldName: string) => void;
  onReorderField: (
    componentId: string,
    fieldName: string,
    direction: "up" | "down"
  ) => void;
  getFields: (componentId: string) => FieldDefinition[];
  onContentUpdate: (componentId: string, content: string) => void;
  onUpdateStyle: (componentId: string, updates: Partial<any>) => void;
  onUpdateImageUrl: (componentId: string, imageUrl: string) => void;
  onUpdateGraphConfig?: (
    componentId: string,
    config: Partial<GraphConfig>
  ) => void;
}

/**
 * Detect component type based on alias or fields
 */
function detectComponentType(
  component: NormalizedComponent
): "field" | "table" | "graph" {
  const alias = component.alias.toLowerCase();

  // Graph components (exclude from configuration)
  if (
    alias.includes("chart") ||
    alias.includes("graph") ||
    alias.includes("usage")
  ) {
    return "graph";
  }

  // Table components
  if (
    alias.includes("table") ||
    alias.includes("history") ||
    alias.includes("charges") ||
    alias.includes("payment")
  ) {
    return "table";
  }

  // Default to field-based
  return "field";
}

/**
 * Get icon for component type
 */
function getComponentTypeIcon(type: "field" | "table" | "graph") {
  switch (type) {
    case "table":
      return <Table size={16} className="text-blue-500" />;
    case "graph":
      return <BarChart3 size={16} className="text-green-500" />;
    default:
      return <FileText size={16} className="text-gray-500" />;
  }
}

/**
 * Field Editor Component
 */
const FieldEditor: React.FC<{
  field: FieldDefinition;
  componentId: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUpdate: (updates: any) => void;
  onToggle: () => void;
  onToggleLabel: () => void;
  onReorder: (direction: "up" | "down") => void;
}> = ({
  field,
  componentId,
  canMoveUp,
  canMoveDown,
  onUpdate,
  onToggle,
  onToggleLabel,
  onReorder,
}) => {
  const [editedLabel, setEditedLabel] = useState(field.label);
  const isModified = editedLabel !== field.label;

  const handleSave = () => {
    onUpdate({ label: editedLabel });
  };

  const handleCancel = () => {
    setEditedLabel(field.label);
  };

  // Update local state if field.label changes from props (e.g. on mount or side switch)
  useEffect(() => {
    setEditedLabel(field.label);
  }, [field.label]);

  return (
    <div
      className={`bg-white border rounded-xl overflow-hidden mb-4 shadow-sm hover:shadow-md transition-all duration-200 ${
        !field.isActive ? "opacity-60" : ""
      }`}
    >
      <div className="p-4">
        {/* Top row: Reorder arrows, name, and visibility toggle */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            {/* Reorder Arrows */}
            <div className="flex flex-col items-center text-gray-400">
              <button
                onClick={() => onReorder("up")}
                disabled={!canMoveUp}
                className={`p-0.5 hover:text-blue-600 transition-colors ${
                  !canMoveUp ? "opacity-20 cursor-not-allowed" : ""
                }`}
                title="Move up"
              >
                <ChevronUp size={18} />
              </button>
              <button
                onClick={() => onReorder("down")}
                disabled={!canMoveDown}
                className={`p-0.5 hover:text-blue-600 transition-colors ${
                  !canMoveDown ? "opacity-20 cursor-not-allowed" : ""
                }`}
                title="Move down"
              >
                <ChevronDown size={18} />
              </button>
            </div>

            {/* Field Name */}
            <div className="font-bold text-gray-800 text-sm tracking-tight truncate">
              {field.label || field.fieldName}
            </div>
          </div>

          {/* Visibility Toggle */}
          <Switch
            checked={field.isActive}
            onCheckedChange={onToggle}
            className="data-[state=checked]:bg-blue-600"
          />
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 my-4" />

        {/* Label Settings (Only if active) */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-600">
              Show Label
            </span>
            <Switch
              checked={field.showLabel}
              onCheckedChange={onToggleLabel}
              disabled={!field.isActive}
              className="data-[state=checked]:bg-blue-600"
            />
          </div>

          {field.showLabel && field.isActive && (
            <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Label Text
                </label>
                {isModified && (
                  <div className="flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200">
                    <button
                      onClick={handleCancel}
                      className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-tight transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="text-[10px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-tight transition-colors"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
              <Input
                value={editedLabel}
                onChange={(e) => setEditedLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
                className="h-10 border-gray-200 focus:ring-1 focus:ring-blue-500 rounded-lg text-sm bg-white"
                placeholder="Enter label text..."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Graph Configuration Component
 */
const GraphConfiguration: React.FC<{
  component: NormalizedComponent;
  onUpdateConfig: (componentId: string, config: Partial<GraphConfig>) => void;
}> = ({ component, onUpdateConfig }) => {
  const config = component.graphConfig || {
    utilityService: "Gas",
    numberOfMonths: 6,
    showXAxis: true,
    showYAxis: true,
    showValues: true,
  };

  // Fetch active utility services from API
  const { data: utilityServicesData } = useActiveUtilityServices();
  const activeServices =
    utilityServicesData?.filter((s: any) => s.isActive) || [];

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={16} className="text-green-500" />
        <h4 className="font-semibold text-gray-800">Graph Configuration</h4>
      </div>

      {/* Utility Service */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Utility Service
        </label>
        <Select
          value={config.utilityService}
          onValueChange={(value) =>
            onUpdateConfig(component.id, { utilityService: value })
          }
        >
          <SelectTrigger className="w-full bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {activeServices.length > 0 ? (
              activeServices.map((service: any) => (
                <SelectItem
                  key={service.id || service.name}
                  value={service.name}
                >
                  {service.name}
                </SelectItem>
              ))
            ) : (
              <>
                <SelectItem value="Gas">Gas</SelectItem>
                <SelectItem value="Water">Water</SelectItem>
                <SelectItem value="Electric">Electric</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Number of Billings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            Number of Billings (Max 12)
          </label>
          <span className="text-sm font-bold text-blue-600">
            {config.numberOfMonths}
          </span>
        </div>
        <Slider
          value={[config.numberOfMonths]}
          max={12}
          min={3}
          step={1}
          onValueChange={(value) =>
            onUpdateConfig(component.id, { numberOfMonths: value[0] })
          }
          className="w-full"
        />
      </div>

      <Separator />

      {/* Display Options */}
      <div className="space-y-4">
        <h5 className="text-sm font-medium text-gray-700">Display Options</h5>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Show X Axis</span>
          <Switch
            checked={config.showXAxis}
            onCheckedChange={(checked) =>
              onUpdateConfig(component.id, { showXAxis: checked })
            }
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Show Y Axis</span>
          <Switch
            checked={config.showYAxis}
            onCheckedChange={(checked) =>
              onUpdateConfig(component.id, { showYAxis: checked })
            }
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Show Values</span>
          <Switch
            checked={config.showValues}
            onCheckedChange={(checked) =>
              onUpdateConfig(component.id, { showValues: checked })
            }
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Component Configuration Panel
 */
const ComponentPanel: React.FC<{
  component: NormalizedComponent;
  fields: FieldDefinition[];
  onFieldUpdate: (fieldName: string, updates: any) => void;
  onToggleField: (fieldName: string) => void;
  onToggleLabel: (fieldName: string) => void;
  onReorderField: (fieldName: string, direction: "up" | "down") => void;
  onContentUpdate: (componentId: string, content: string) => void;
  onUpdateStyle: (componentId: string, updates: Partial<any>) => void;
  onUpdateImageUrl: (componentId: string, imageUrl: string) => void;
  onUpdateGraphConfig?: (
    componentId: string,
    config: Partial<GraphConfig>
  ) => void;
}> = ({
  component,
  fields,
  onFieldUpdate,
  onToggleField,
  onToggleLabel,
  onReorderField,
  onContentUpdate,
  onUpdateStyle,
  onUpdateImageUrl,
  onUpdateGraphConfig,
}) => {
  const [editedContent, setEditedContent] = useState(component.content || "");
  const [editedImageUrl, setEditedImageUrl] = useState(
    component.imageUrl || ""
  );


  // Sync with component changes
  useEffect(() => {
    setEditedContent(component.content || "");
    setEditedImageUrl(component.imageUrl || "");
  }, [component.id, component.content, component.imageUrl]);

  const isContentModified = editedContent !== (component.content || "");
  const isImageUrlModified = editedImageUrl !== (component.imageUrl || "");

  const handleSaveContent = () => {
    onContentUpdate(component.id, editedContent);
  };

  const handleCancelContent = () => {
    setEditedContent(component.content || "");
  };

  const handleSaveImageUrl = () => {
    onUpdateImageUrl(component.id, editedImageUrl);
  };

  const handleCancelImageUrl = () => {
    setEditedImageUrl(component.imageUrl || "");
  };

  const componentType = detectComponentType(component);

  // Exclude graph components
  // Graph components
  // Graph components
  if (componentType === "graph") {
    if (onUpdateGraphConfig) {
      return (
        <GraphConfiguration
          component={component}
          onUpdateConfig={onUpdateGraphConfig}
        />
      );
    }
    return (
      <div className="p-4 text-center text-gray-500">
        <BarChart3 size={48} className="mx-auto mb-2 opacity-50" />
        <p>Graph components are not configurable</p>
      </div>
    );
  }

  if (component.alias === "text_box") {
    return (
      <div className="mb-6 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] font-bold text-blue-700 uppercase tracking-tight">
            Rich Text Editor
          </label>
          {isContentModified && (
            <div className="flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200">
              <button
                onClick={handleCancelContent}
                className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-tight transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveContent}
                className="text-[10px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-tight transition-colors"
              >
                Save
              </button>
            </div>
          )}
        </div>
        <div className="space-y-3">
          <textarea
            className="w-full min-h-[100px] p-2 text-sm border rounded focus:ring-1 focus:ring-blue-400 outline-none"
            placeholder="Enter text content here..."
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
          />

          <div className="flex flex-wrap gap-2">
            {/* Quick Styles */}
            <div className="flex bg-white border rounded p-1">
              <Button
                variant="ghost"
                size="icon"
                className={`h-7 w-7 ${
                  component.style?.fontWeight === "bold" ? "bg-gray-100" : ""
                }`}
                onClick={() =>
                  onUpdateStyle(component.id, {
                    fontWeight:
                      component.style?.fontWeight === "bold"
                        ? "normal"
                        : "bold",
                  })
                }
              >
                <span className="font-bold">B</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-7 w-7 ${
                  component.style?.fontStyle === "italic" ? "bg-gray-100" : ""
                }`}
                onClick={() =>
                  onUpdateStyle(component.id, {
                    fontStyle:
                      component.style?.fontStyle === "italic"
                        ? "normal"
                        : "italic",
                  })
                }
              >
                <span className="italic">I</span>
              </Button>
            </div>

            {/* Alignment */}
            <div className="flex bg-white border rounded p-1">
              {(["left", "center", "right"] as const).map((align) => (
                <Button
                  key={align}
                  variant="ghost"
                  size="icon"
                  className={`h-7 w-7 ${
                    component.style?.textAlign === align ? "bg-gray-100" : ""
                  }`}
                  onClick={() =>
                    onUpdateStyle(component.id, { textAlign: align })
                  }
                >
                  <span className="text-[10px] uppercase">{align[0]}</span>
                </Button>
              ))}
            </div>

            {/* Font Size */}
            <div className="flex items-center gap-2 bg-white border rounded px-2 py-1">
              <span className="text-[10px] text-gray-500">Size</span>
              <input
                type="number"
                className="w-12 text-xs outline-none"
                value={component.style?.fontSize || 14}
                onChange={(e) =>
                  onUpdateStyle(component.id, {
                    fontSize: parseInt(e.target.value) || 14,
                  })
                }
              />
            </div>

            {/* Color */}
            <div className="flex items-center gap-2 bg-white border rounded px-2 py-1">
              <span className="text-[10px] text-gray-500">Color</span>
              <input
                type="color"
                className="w-6 h-4 p-0 border-0 outline-none pointer cursor-pointer"
                value={component.style?.color || "#374151"}
                onChange={(e) =>
                  onUpdateStyle(component.id, { color: e.target.value })
                }
              />
            </div>
          </div>
        </div>
        <Separator className="my-4" />
      </div>
    );
  }

  // No fields
  if (fields.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <FileText size={48} className="mx-auto mb-2 opacity-50" />
        <p>No fields available for this component</p>
      </div>
    );
  }

  // Sort fields by order
  const sortedFields = [...fields].sort((a, b) => a.order - b.order);

  return (
    <div className="p-4">
      {/* Component Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          {getComponentTypeIcon(componentType)}
          <h4 className="font-semibold text-gray-800">{component.type}</h4>
        </div>
        <div className="text-xs text-gray-500">
          {componentType === "table"
            ? "Table Column Configuration"
            : "Field Configuration"}
        </div>
        <div className="text-xs text-gray-400 mt-1">
          {sortedFields.filter((f) => f.isActive).length} of{" "}
          {sortedFields.length}{" "}
          {componentType === "table" ? "columns" : "fields"} visible
        </div>
      </div>

      <Separator className="mb-4" />

      {/* Rich Text Editor for Text Box */}

      {/* Logo URL Input for Logo */}
      {component.alias === "logo" && (
        <div className="mb-6 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-bold text-blue-700 uppercase tracking-tight">
              Logo Configuration
            </label>
            {isImageUrlModified && (
              <div className="flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200">
                <button
                  onClick={handleCancelImageUrl}
                  className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-tight transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveImageUrl}
                  className="text-[10px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-tight transition-colors"
                >
                  Save
                </button>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                Image URL
              </span>
              <Input
                className="h-9 text-sm border-gray-200 focus:ring-1 focus:ring-blue-400 outline-none w-full bg-white"
                placeholder="https://example.com/logo.png"
                value={editedImageUrl}
                onChange={(e) => setEditedImageUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveImageUrl();
                  if (e.key === "Escape") handleCancelImageUrl();
                }}
              />
            </div>
            <p className="text-[10px] text-gray-400 italic">
              * Enter a direct URL to an image (PNG, JPG, or SVG).
            </p>
          </div>
          <Separator className="my-4" />
        </div>
      )}

      {/* Field List */}
      <ScrollArea className="h-[400px]">
        <div className="space-y-0">
          {sortedFields.map((field, index) => (
            <FieldEditor
              key={field.fieldName}
              field={field}
              componentId={component.id}
              canMoveUp={index > 0}
              canMoveDown={index < sortedFields.length - 1}
              onUpdate={(updates) => onFieldUpdate(field.fieldName, updates)}
              onToggle={() => onToggleField(field.fieldName)}
              onToggleLabel={() => onToggleLabel(field.fieldName)}
              onReorder={(direction) =>
                onReorderField(field.fieldName, direction)
              }
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

/**
 * Main Configuration Tab
 */
export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  components,
  selectedComponentId,
  onFieldUpdate,
  onToggleField,
  onToggleLabel,
  onReorderField,
  getFields,
  onContentUpdate,
  onUpdateStyle,
  onUpdateImageUrl,
  onUpdateGraphConfig,
}) => {
  const selectedComponent = components.find(
    (c) => c.id === selectedComponentId
  );

  if (!selectedComponent) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <FileText size={64} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg">No component selected</p>
          <p className="text-sm mt-2">
            Select a component from the canvas to configure its fields
          </p>
        </div>
      </div>
    );
  }

  const fields = getFields(selectedComponent.id);

  return (
    <div className="h-full bg-gray-50/50">
      <ComponentPanel
        component={selectedComponent}
        fields={fields}
        onFieldUpdate={(fieldName, updates) =>
          onFieldUpdate(selectedComponent.id, fieldName, updates)
        }
        onToggleField={(fieldName) =>
          onToggleField(selectedComponent.id, fieldName)
        }
        onToggleLabel={(fieldName) =>
          onToggleLabel(selectedComponent.id, fieldName)
        }
        onReorderField={(fieldName, direction) =>
          onReorderField(selectedComponent.id, fieldName, direction)
        }
        onContentUpdate={onContentUpdate}
        onUpdateStyle={onUpdateStyle}
        onUpdateImageUrl={onUpdateImageUrl}
        onUpdateGraphConfig={onUpdateGraphConfig}
      />
    </div>
  );
};

export default ConfigurationTab;
